import React, { useState } from 'react';

const TOOLTIPS = {
  'Bid/ask spread': 'The gap between what buyers will pay and sellers will accept, as a % of the mid price. Tighter spreads mean better fills. Above 10% makes fills unreliable.',
  'Open interest': 'Total outstanding option contracts. Higher OI means more liquidity. Below 500 risks difficulty entering or exiting.',
  'Volume': 'Contracts traded today. Low volume means your order may move the market against you.',
  'Delta': 'How much the option price moves per $1 move in the stock. Also approximates the probability the option expires in the money.',
  'Weekly yield': 'Fill-adjusted premium divided by strike, expressed as a weekly return. Target is 1% or more.',
  'DTE': 'Days to expiration. Target 21–45 DTE to balance premium decay with time for the trade to work.',
  'Fill-adjusted premium': 'Expected premium after slippage. Fill factor (25% default) is applied to the mid price to reflect real-world fills.',
  'Downside protection': 'How far the stock can fall before your position is underwater. Strike distance from current price.',
  'Current regime': 'Whether the underlying is trending up, sideways, down, or volatile — based on moving averages and realized vol.',
  'Score': 'Ranking score combining IV Rank, yield, capital efficiency, downside protection, and probability of profit.',
  'Capital at risk': 'Maximum capital needed if assigned — strike × 100 shares per contract. Becomes your cost basis.',
  'Expected premium': 'Fill-adjusted premium × 100 shares. The cash you collect upfront.',
};

function Tooltip({ text }) {
  const [visible, setVisible] = useState(false);
  return (
    <span style={{ position: 'relative', display: 'inline-block', marginLeft: 6, verticalAlign: 'middle' }}>
      <span
        onMouseEnter={() => setVisible(true)}
        onMouseLeave={() => setVisible(false)}
        style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 15, height: 15, borderRadius: '50%', border: '1px solid #475569', color: '#64748b', fontSize: 9, fontWeight: 600, cursor: 'default', userSelect: 'none' }}
      >i</span>
      {visible && (
        <div style={{ position: 'absolute', bottom: '100%', left: 0, marginBottom: 6, width: 240, background: '#0f1520', border: '0.5px solid #334155', borderRadius: 8, padding: '8px 12px', fontSize: 12, color: '#cbd5e1', lineHeight: 1.5, zIndex: 200, pointerEvents: 'none', boxShadow: '0 4px 20px rgba(0,0,0,0.5)' }}>
          {text}
        </div>
      )}
    </span>
  );
}

function Check({ pass }) {
  return (
    <span style={{ fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 4, background: pass ? '#14352a' : '#3d1515', color: pass ? '#4ade80' : '#fca5a5', marginLeft: 6 }}>
      {pass ? 'PASS' : 'FLAG'}
    </span>
  );
}

function Row({ label, value, pass, note }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', padding: '11px 0', borderBottom: '0.5px solid #2d3748' }}>
      <div>
        <div style={{ fontSize: 14, color: '#cbd5e1', display: 'flex', alignItems: 'center' }}>
          {label}
          {TOOLTIPS[label] && <Tooltip text={TOOLTIPS[label]} />}
        </div>
        {note && <div style={{ fontSize: 11, color: '#64748b', marginTop: 2 }}>{note}</div>}
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, textAlign: 'right', flexShrink: 0, marginLeft: 12 }}>
        <span style={{ fontSize: 14, color: '#f1f5f9', fontWeight: 500 }}>{value}</span>
        {pass !== undefined && <Check pass={pass} />}
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div style={{ marginBottom: 16 }}>
      <div style={{ fontSize: 11, fontWeight: 500, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 2, paddingBottom: 6, borderBottom: '0.5px solid #1e2d3d' }}>{title}</div>
      {children}
    </div>
  );
}

export default function DecisionPanel({ opportunity, ruleSet, onClose }) {
  if (!opportunity) return null;
  const o = opportunity;
  const rs = ruleSet;

  const deltaOk = o.delta >= parseFloat(rs.target_delta_min) && o.delta <= parseFloat(rs.target_delta_max);
  const yieldOk = o.weeklyYield >= parseFloat(rs.min_premium_yield);
  const oiOk = o.openInterest >= rs.min_open_interest;
  const volumeOk = o.volume >= rs.min_volume;
  const spreadOk = o.spreadPct <= parseFloat(rs.max_spread_pct);
  const dteOk = o.dte >= rs.min_dte && o.dte <= rs.max_dte;
  const hasIVRank = o.ivRank !== undefined && o.ivRank !== null;
  const allRulesPass = deltaOk && yieldOk && oiOk && volumeOk && spreadOk && dteOk;

  const regimeNote = {
    DOWNTREND: 'Downtrend — assignment risk elevated. Verify strike cushion.',
    VOLATILE: 'Volatility expansion — IV may be structural, not just opportunity.',
    UPTREND: 'Uptrend. Standard recommendation tone.',
    SIDEWAYS: 'Sideways — optimal wheel conditions.',
  }[o.regimeFlag] || '';

  const regimePass = o.regimeFlag === 'UPTREND' || o.regimeFlag === 'SIDEWAYS';
  const totalPremium = (o.fillAdjustedPremium * 100).toFixed(2);
  const capitalAtRisk = (o.strike * 100).toFixed(2);
  const ivRankColor = !hasIVRank ? '#94a3b8' : o.ivRank >= 50 ? '#4ade80' : o.ivRank >= 30 ? '#fbbf24' : '#fca5a5';

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', display: 'flex', justifyContent: 'flex-end', zIndex: 100 }} onClick={onClose}>
      <div style={{ background: '#1e2533', borderLeft: '0.5px solid #2d3748', width: 520, height: '100%', display: 'flex', flexDirection: 'column' }} onClick={e => e.stopPropagation()}>

        <div style={{ flex: 1, overflowY: 'auto', padding: '24px 24px 0' }}>

          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 }}>
            <div>
              <div style={{ fontSize: 11, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 4 }}>Recommendation</div>
              <div style={{ fontSize: 20, fontWeight: 500, color: '#f1f5f9' }}>
                Sell ${o.strike.toFixed(2)} put &middot; {new Date(o.expiration).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
              </div>
              <div style={{ fontSize: 13, color: '#94a3b8', marginTop: 4, display: 'flex', alignItems: 'center', gap: 12 }}>
                <span>{o.ticker} &middot; {o.dte} DTE &middot; Expected ${totalPremium}/contract</span>
                {hasIVRank && <span style={{ color: ivRankColor, fontWeight: 600 }}>IV Rank {o.ivRank}</span>}
              </div>
            </div>
            <button onClick={onClose} style={{ background: 'none', border: 'none', color: '#64748b', fontSize: 18, cursor: 'pointer', padding: 4 }}>✕</button>
          </div>

          <div style={{ background: allRulesPass && regimePass ? '#0f2d1f' : '#2d1f0f', border: '0.5px solid ' + (allRulesPass && regimePass ? '#166534' : '#854d0e'), borderRadius: 8, padding: '12px 16px', marginBottom: 20 }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: allRulesPass && regimePass ? '#4ade80' : '#fbbf24', marginBottom: 4 }}>
              {allRulesPass && regimePass ? '✓ All filters passed — trade aligned with system' : '⚠ Review regime flag before trading'}
            </div>
            <div style={{ fontSize: 12, color: '#94a3b8', lineHeight: 1.5 }}>
              {allRulesPass && regimePass
                ? 'Sell to open ' + o.ticker + ' $' + o.strike + ' put exp ' + o.expiration + '. Target fill: $' + o.fillAdjustedPremium.toFixed(2) + '. Capital at risk: $' + capitalAtRisk + '/contract.'
                : regimeNote}
            </div>
          </div>

          <Section title="Data quality">
            <Row label="Bid/ask spread" value={(o.spreadPct * 100).toFixed(1) + '%'} pass={spreadOk} note={spreadOk ? 'Within max spread threshold' : 'Exceeds ' + (parseFloat(rs.max_spread_pct) * 100) + '% max'} />
            <Row label="Open interest" value={o.openInterest.toLocaleString()} pass={oiOk} note={oiOk ? 'Adequate liquidity' : 'Below ' + rs.min_open_interest + ' minimum'} />
            <Row label="Volume" value={o.volume.toLocaleString()} pass={volumeOk} note={volumeOk ? 'Adequate volume' : 'Below ' + rs.min_volume + ' minimum'} />
          </Section>

          <Section title="Rule alignment">
            <Row label="Delta" value={o.delta.toFixed(3)} pass={deltaOk} note={'Target: ' + rs.target_delta_min + '–' + rs.target_delta_max} />
            <Row label="Weekly yield" value={(o.weeklyYield * 100).toFixed(2) + '%'} pass={yieldOk} note={'Min: ' + (parseFloat(rs.min_premium_yield) * 100).toFixed(1) + '% / week'} />
            <Row label="DTE" value={o.dte + ' days'} pass={dteOk} note={'Target: ' + rs.min_dte + '–' + rs.max_dte + ' days'} />
            <Row label="Fill-adjusted premium" value={'$' + o.fillAdjustedPremium.toFixed(2)} note={'Fill factor ' + (parseFloat(rs.fill_factor) * 100).toFixed(0) + '% applied to mid $' + o.mid.toFixed(2)} />
            <Row label="Downside protection" value={(o.downsideProtection * 100).toFixed(1) + '%'} note="Strike distance from current price" />
          </Section>

          <Section title="Underlying regime">
            <Row label="Current regime" value={o.regimeFlag} pass={regimePass} note={regimeNote} />
          </Section>

          <Section title="Capital efficiency">
            <Row label="Score" value={o.capitalEfficiency.toFixed(6)} note="Premium yield per dollar deployed per day." />
            <Row label="Capital at risk" value={'$' + capitalAtRisk + ' / contract'} note={'Strike $' + o.strike + ' x 100 shares'} />
            <Row label="Expected premium" value={'$' + totalPremium + ' / contract'} note="Fill-adjusted estimate" />
          </Section>

        </div>

        <div style={{ padding: '16px 24px 28px', borderTop: '0.5px solid #2d3748', background: '#1e2533', flexShrink: 0 }}>
          <div style={{ fontSize: 12, color: '#475569', marginBottom: 10 }}>If you place this trade in eTrade, log it in the ledger to begin tracking it.</div>
          <button style={{ width: '100%', fontSize: 14, fontWeight: 500, color: '#f1f5f9', background: '#2563eb', border: 'none', borderRadius: 8, padding: '13px 0', cursor: 'pointer' }} onClick={onClose}>
            Close and log trade in ledger →
          </button>
        </div>

      </div>
    </div>
  );
}
